"use client";

import { getDiscountPercentage, isOnSale, type SalePricing } from "@/lib/sale-utils";
import { isPwywAsset, type PwywPricing } from "@/lib/pwyw";
import { cn } from "@/lib/utils";

export type DiscountableAsset = SalePricing & PwywPricing & { is_free?: boolean | null };

/**
 * The discount a buyer actually gets, or 0 when there isn't one.
 *
 * Free and pay-what-you-want assets can carry a stale `sale_price` from a
 * previous fixed-price life, so they are excluded before `isOnSale` is asked -
 * the same order `AssetPrice` uses, so a card's badge and its price can never
 * disagree. Outside React, use `listingPrice(asset).discountPercent` from
 * `@/lib/listing-price`, which follows the same precedence.
 */
export function discountPercent(asset: DiscountableAsset): number {
  if (asset.is_free || isPwywAsset(asset)) return 0;
  if (!isOnSale(asset)) return 0;
  return getDiscountPercentage(asset);
}

/**
 * The single sale marker across the marketplace: the size of the discount, not
 * the word "Sale". A number is the thing that moves a buyer, and it costs the
 * same pixels.
 *
 * It is filled `primary` rather than `destructive` because it sits on top of
 * product photography and has to stay legible: white on `--destructive` is
 * 4.05:1 in light mode, under the 4.5:1 AA floor for text this size, while
 * white on `--primary` clears it in both themes (6.1:1 light, 4.8:1 dark).
 */
export function DiscountBadge({
  asset,
  className,
}: {
  asset: DiscountableAsset;
  className?: string;
}) {
  const pct = discountPercent(asset);
  if (pct <= 0) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full bg-primary px-2 py-0.5 font-mono text-2xs font-bold tabular-nums text-primary-foreground",
        className,
      )}
    >
      -{pct}%
    </span>
  );
}
